import React, { useState } from "react";
import { Loader2, CreditCard } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { EnrollmentBillingItem } from "../../api/getEnrollmentBilling/types";
import { collectEnrollmentPayment } from "../../api/collectEnrollmentPayment/collectEnrollmentPaymentApi";

interface SecureData {
  authData: {
    clientKey: string;
    apiLoginID: string;
  };
  cardData: {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
    fullName: string;
  };
}

interface AcceptResponse {
  messages: {
    resultCode: string;
    message: Array<{ code: string; text: string }>;
  };
  opaqueData: {
    dataDescriptor: string;
    dataValue: string;
  };
}

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: SecureData,
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
  }
}

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

function formatCardNumber(value: string): string {
  const numericValue = value.replace(/\D/g, "");
  return numericValue.replace(/(.{4})/g, "$1 ").trim();
}

interface CollectPaymentDrawerProps {
  enrollment: EnrollmentBillingItem | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CollectPaymentDrawer({
  enrollment,
  open,
  onClose,
  onSuccess,
}: CollectPaymentDrawerProps) {
  const { toast } = useToast();
  const [processing, setProcessing] = useState(false);
  const [showNewCard, setShowNewCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Card form state
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardName, setCardName] = useState("");

  if (!enrollment) return null;

  const hasStoredProfile =
    !!enrollment.storedCustomerProfileId && !!enrollment.storedPaymentProfileId;
  const seatPrice = enrollment.seatPrice;

  const handleChargeStoredProfile = async () => {
    setProcessing(true);
    setError(null);
    try {
      await collectEnrollmentPayment(enrollment.enrollmentId, {
        useStoredProfile: true,
      });
      toast({
        title: "Payment collected",
        description: fmt(seatPrice) + " charged successfully.",
      });
      onClose();
      onSuccess();
    } catch (e: any) {
      const msg =
        e?.response?.data?.errors?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Payment failed";
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleChargeNewCard = async () => {
    if (!cardNumber || !expMonth || !expYear || !cvv) {
      setError("All card fields are required.");
      return;
    }

    const apiLoginID = process.env.REACT_APP_AUTHNET_API_LOGIN_ID || "";
    const clientKey = process.env.REACT_APP_AUTHNET_CLIENT_KEY || "";

    if (!apiLoginID || !clientKey) {
      setError("Payment credentials are not configured.");
      return;
    }

    if (!window.Accept) {
      setError("Payment system not loaded. Please refresh the page.");
      return;
    }

    setProcessing(true);
    setError(null);

    window.Accept.dispatchData(
      {
        authData: { clientKey, apiLoginID },
        cardData: {
          cardNumber: cardNumber.replace(/\s/g, ""),
          month: expMonth,
          year: expYear,
          cardCode: cvv,
          fullName: cardName,
        },
      },
      async (response: AcceptResponse) => {
        if (response.messages.resultCode === "Error") {
          setError(
            response.messages.message[0]?.text || "Card validation failed",
          );
          setProcessing(false);
          return;
        }

        try {
          await collectEnrollmentPayment(enrollment.enrollmentId, {
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue,
          });
          toast({
            title: "Payment collected",
            description: fmt(seatPrice) + " charged successfully.",
          });
          onClose();
          onSuccess();
        } catch (e: any) {
          const msg =
            e?.response?.data?.errors?.detail ||
            e?.response?.data?.message ||
            e?.message ||
            "Payment failed";
          setError(msg);
        } finally {
          setProcessing(false);
        }
      },
    );
  };

  return (
    <Sheet onOpenChange={(o) => !o && onClose()} open={open}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-base font-normal text-gray-900 dark:text-white">
            Collect Payment
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500 dark:text-gray-400">
            {enrollment.firstName} {enrollment.lastName}
            {enrollment.companyName ? ` · ${enrollment.companyName}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Amount */}
          <div className="flex items-center justify-between p-4 rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Amount due
            </span>
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {fmt(seatPrice)}
            </span>
          </div>

          {error && (
            <div className="p-3 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Stored profile option */}
          {hasStoredProfile && !showNewCard && (
            <div className="space-y-3">
              <Button
                className="w-full h-10"
                disabled={processing}
                onClick={handleChargeStoredProfile}
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Charge {enrollment.storedCardType || "Card"} ••
                {enrollment.storedCardLast4 || "****"} — {fmt(seatPrice)}
              </Button>
              <button
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                onClick={() => setShowNewCard(true)}
                type="button"
              >
                Use a different card →
              </button>
            </div>
          )}

          {/* New card form */}
          {(!hasStoredProfile || showNewCard) && (
            <div className="space-y-3">
              {showNewCard && (
                <button
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors mb-2"
                  onClick={() => setShowNewCard(false)}
                  type="button"
                >
                  ← Use card on file
                </button>
              )}
              <Input
                disabled={processing}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="Name on card"
                value={cardName}
              />
              <Input
                disabled={processing}
                maxLength={19}
                onChange={(e) =>
                  setCardNumber(formatCardNumber(e.target.value))
                }
                placeholder="Card number"
                value={cardNumber}
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  disabled={processing}
                  maxLength={2}
                  onChange={(e) =>
                    setExpMonth(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="MM"
                  value={expMonth}
                />
                <Input
                  disabled={processing}
                  maxLength={4}
                  onChange={(e) =>
                    setExpYear(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="YYYY"
                  value={expYear}
                />
                <Input
                  disabled={processing}
                  maxLength={4}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))}
                  placeholder="CVV"
                  type="password"
                  value={cvv}
                />
              </div>
              <Button
                className="w-full h-10"
                disabled={processing}
                onClick={handleChargeNewCard}
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Charge {fmt(seatPrice)}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
